import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import { listAudits } from '../api';
import { AuditRun } from '../types';
import { formatAuditLabel } from '../utils/auditTitle';
import { useAuditBasePath } from '../utils/useAuditBasePath';

const COLLAPSE_KEY = 'bp-audit-history-collapsed';

export const AuditLayout = () => {
  const { auditId } = useParams<{ auditId?: string }>();

  // History sidebar is collapsible — once you're inside an audit the run list is rarely needed,
  // and collapsing it frees horizontal room for the in-audit section rail. Persisted so the
  // choice survives reloads.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSE_KEY) === '1'
  );
  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const auditsQuery = useQuery({
    queryKey: ['audits'],
    queryFn: listAudits
  });

  return (
    <div className='audit-layout'>
      <aside className={classNames('audit-sidebar', { 'audit-sidebar--collapsed': collapsed })}>
        <div className='audit-sidebar__header'>
          <button
            type='button'
            className='audit-sidebar__toggle'
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand history' : 'Collapse history'}
            aria-label={collapsed ? 'Expand history' : 'Collapse history'}
          >
            {collapsed ? '»' : '«'}
          </button>
          {!collapsed && (
            <div className='audit-sidebar__heading'>
              <div className='audit-sidebar__brand'>Bliss Point Audit</div>
              <div className='audit-sidebar__company'>Away</div>
            </div>
          )}
        </div>

        {!collapsed && (
          <nav className='audit-sidebar__nav'>
            <div className='audit-sidebar__section-label'>History</div>
            {auditsQuery.isLoading && <div className='audit-sidebar__loading'>Loading…</div>}
            {auditsQuery.data?.length === 0 && (
              <div className='audit-sidebar__empty'>No audits yet.</div>
            )}
            {auditsQuery.data?.map(run => (
              <AuditSidebarRow key={run.id} run={run} active={run.id === auditId} />
            ))}
          </nav>
        )}
      </aside>

      <main className='audit-main'>
        <Outlet />
      </main>
    </div>
  );
};

interface AuditSidebarRowProps {
  run: AuditRun;
  active: boolean;
}

const AuditSidebarRow = ({ run, active }: AuditSidebarRowProps) => {
  const navigate = useNavigate();
  const base = useAuditBasePath();
  const statusLabel = formatStatusLabel(run);

  return (
    <button
      type='button'
      className={classNames('audit-sidebar__row', { 'audit-sidebar__row--active': active })}
      onClick={() => navigate(`${base}/${run.id}`)}
    >
      <div className='audit-sidebar__row-title'>{formatAuditLabel(run.triggeredAt)}</div>
      <div className='audit-sidebar__row-meta'>{statusLabel}</div>
    </button>
  );
};

const formatStatusLabel = (run: AuditRun): string => {
  if (run.status === 'complete') return 'Complete';
  if (run.status === 'failed') return 'Failed';
  if (run.status === 'pending') return 'Pending';
  if (run.status === 'pulling') return 'Pulling data…';
  if (run.status === 'analyzing') return 'Analyzing…';
  return run.status;
};
